import Modal from "components/modal/Modal";
import { useEffect, useState } from "react";
import InnerBox from "container/Common/ModalContainer/InnerBox";
import ModalContainer from "container/Common/ModalContainer";
import {
  UserGetFormType,
  UserCreateFormType,
  ValidUserCreateFormType,
  UserUpdateFormType,
  ValidateColorType,
} from "types/admin/AdminType";
import {
  DropDownListOptionStringType,
  DropDownListOptionType,
} from "types/common/CommonType";
import { Util } from "Util";
import { CheckBadge } from "components/badge/CheckBadge";
import useUserAPIStore from "apis/admin/user.api";
import useModalStore from "store/useModalStore";
import useUser from "hooks/useUser";
import { EmptyBox } from "container/Common/ModalContainer/EmptyBox";
import { useNavigate } from "react-router-dom";
import useSearchFieldChange from "hooks/useSearchFieldChange";
import useGroupDropdown from "hooks/Group/useGroupDropdown";
import { ChipArea } from "components/area/Chip";
import useToastStore from "store/useToastStore";
import useUserStore from "store/useUserStore";
import { CheckboxArea } from "components/area/Checkbox";
import { CheckButton } from "components/button/CheckButton";
import { SelectBox } from "components/input/SelectBox/SelectBox";
import useAccessStore from "store/useAccessStore";
import useFocusTrapping from "hooks/useFocusTrapping";
import { TextFieldWithLock } from "container/Common/EditDetailsModal/EditDetailsGeneral/TextFieldWithLock";
import { StyledUserModalContainer } from "./style";
import { Button } from "components/button/Button/Button";

/**
 * @description UserModal 한정
 * @author c_bae
 * @param isValuePresent 값의 유무를 체크하는 역할 (userForm.loginId 등).
 * @param isValid 유효성 검사 결과 (isValidUserForm.loginId.isValidCharacter 등).
 * @returns
 */
const getBadgeColor = (
  isValuePresent: any,
  isValid: boolean
): ValidateColorType => {
  if (!isValuePresent) return "gray"; // 값이 없을 때 기본 색상
  return isValid ? "blue" : "red"; // 값이 있으면 조건에 따라 색상 결정
};

interface UserModalProps {
  canModifyUsersRole?: boolean;

  modalToggle: (state: boolean) => void;
  dropdownOptions: {
    group: DropDownListOptionType[];
    role: DropDownListOptionType[];
  };
  userId?: UserGetFormType["userId"];
}

export const UserModal = ({
  modalToggle,
  dropdownOptions,
  userId,
  canModifyUsersRole,
}: UserModalProps) => {
  const {
    userInfoData: { userId: loginUserId },
  } = useUserStore();
  const { clear, getUserInfo } = useUser();
  const navigate = useNavigate();
  const { activeToast } = useToastStore();

  const { editMode, userModal, modalRef } = useModalStore();

  useFocusTrapping(userModal, modalRef);

  const {
    getUser,
    getUserList,
    createUser,
    getLoginIdCheckDuplicate,
    updateUser,
  } = useUserAPIStore();

  const {
    searchSuggestions,
    handleSearchValue,
    handleSearchValueDatasourceChange,
  } = useSearchFieldChange(dropdownOptions.group);

  const [isAble, setIsAble] = useState<boolean>(false);
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<
    DropDownListOptionStringType[]
  >([]);
  const [sortedDropdownOptionsRoles, setSortedDropdownOptionsRoles] = useState<
    DropDownListOptionType[]
  >(dropdownOptions.role);
  const [selectedRoles, setSelectedRoles] = useState<DropDownListOptionType[]>(
    []
  );
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [userForm, setUserForm] = useState<UserCreateFormType>({
    loginId: "",
    password: "",
    username: "",
    groups: [],
    mobile: "",
    email: "",
    roles: [],
  });

  const [userFormOriginal, setUserFormOriginal] = useState<UserUpdateFormType>(
    {}
  );
  const [isValidUserForm, setIsValidUserForm] =
    useState<ValidUserCreateFormType>({
      loginId: {
        isValidLength: false,
        isValidDuplicate: "None",
        isValidCharacter: false,
      },
      password: {
        isValidLowerLetter: false,
        isValidNumber: false,
        isValidSpecialLetter: false,
        isValidLength: false,
      },
      passwordConfirm: {
        isValidEqualPassword: false,
      },
    });

  const isValidForCreated =
    isValidUserForm.loginId.isValidLength &&
    isValidUserForm.loginId.isValidDuplicate === true &&
    isValidUserForm.password.isValidLength &&
    isValidUserForm.password.isValidLowerLetter &&
    isValidUserForm.password.isValidNumber &&
    isValidUserForm.password.isValidSpecialLetter &&
    isValidUserForm.passwordConfirm.isValidEqualPassword;

  const changedForm = Util.getDifferentOnSecondObjectAndNullAble(
    userFormOriginal,
    userForm
  );

  const selectedGroupLabels = new Set(
    selectedGroups.map((group) => group.label)
  );

  const filteredSearchSuggestions = searchSuggestions.filter(
    (suggestion) => !selectedGroupLabels.has(suggestion)
  );

  const { getSelectedGroupInfo } = useGroupDropdown();

  const handleGroupSelect = (selectedGroupName: string) => {
    const selectedGroupInfo = getSelectedGroupInfo(
      selectedGroupName,
      dropdownOptions.group as DropDownListOptionStringType[]
    );

    /** 선택한 그룹이 그룹목록에서 일치하지 않는 경우 */
    if (!selectedGroupInfo) {
      return;
    }

    setSelectedGroups((prev) => [...prev, selectedGroupInfo]);
    handleSearchValue("");
    handleSearchValueDatasourceChange("");
  };

  const handleSelectedGroupExclude = (excludeGroupName: string) => {
    setSelectedGroups((prev) => [
      ...prev.filter((item) => item.label !== excludeGroupName),
    ]);
  };

  const handleSubmitButtonClick = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();

    setIsSubmitted(true);

    /** getDifferentOnSecondObjectAndNullAble 에서 group이랑 roles 는 변경되지 않았으면 포함시키지 않는 상황이라 수동으로 추가 */
    if (!changedForm.groups) {
      changedForm.groups = userFormOriginal.groups;
    }

    if (!changedForm.roles) {
      changedForm.roles = userFormOriginal.roles;
    }

    const res =
      editMode && userId
        ? await updateUser(
            {
              ...changedForm,
              username: !changedForm.username
                ? userForm.username
                : (changedForm.username as string),
            },
            userId
          )
        : await createUser(userForm);

    if (res.code === 200) {
      activeToast({
        message: editMode
          ? "User information has been modified."
          : "User information has been registered.",
        type: "success",
        timeout: 3000,
      });
      modalToggle(false);
      getUserList();

      const userInfo = await getUserInfo();

      if (
        editMode &&
        Object.keys(changedForm).includes("loginId") &&
        userInfo?.userId === userId
      ) {
        activeToast({
          message:
            "The login ID has been changed. You will be logged out for re-login.",
          type: "info",
          timeout: 3000,
        });

        setTimeout(() => {
          clear();
          navigate("/login");
          // window.location.replace("/#/login");
        }, 3000);
      }

      return;
    }
    activeToast({
      message: res?.message ?? "Failed to modify user information.",
      type: "error",
      timeout: 3000,
    });

    setIsSubmitted(false);
  };

  // groups & roles 제외
  const handleUserFormChange = (name: string, value: string) => {
    const updatedForm = { ...userForm, [name]: value };
    setUserForm(updatedForm);
  };

  const handlePasswordConfirmChange = (name: string, value: string) => {
    setPasswordConfirm(value);
  };

  /** 아이디 중복체크 */
  const handleIdCheckDuplicate = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (
      isValidUserForm.loginId.isValidLength &&
      isValidUserForm.loginId.isValidCharacter &&
      userForm.loginId &&
      userForm.loginId !== userFormOriginal.loginId
    ) {
      const res = await getLoginIdCheckDuplicate(userForm.loginId);

      setIsValidUserForm({
        ...isValidUserForm,
        loginId: {
          ...isValidUserForm.loginId,
          isValidDuplicate: !res,
        },
      });
    }
  };

  const { modifyRoles: modifyRolesRole } = useAccessStore(
    (state) => state.accessRole.roles
  );

  useEffect(
    /** 아이디 유효성검사 및 중복체크*/
    function updateIsValidLoginId() {
      const checkValidLength = Util.checkValidateUser(
        "loginId",
        userForm?.loginId,
        "isValidLength"
      );

      const checkValidCharacter = Util.checkValidateUser(
        "loginId",
        userForm?.loginId,
        "isValidCharacter"
      );

      setIsValidUserForm({
        ...isValidUserForm,
        loginId: {
          isValidDuplicate: "None",
          isValidLength: checkValidLength,
          isValidCharacter: checkValidCharacter,
        },
      });
    },
    [userForm.loginId]
  );

  useEffect(
    /** 수정화면 데이터 세팅 */
    function fetchInitialUserDataInEditMode() {
      if (!(userId && editMode)) {
        return;
      }

      const getUserInfoByUserId = async (id: string) => {
        const { loginId, username, groups, mobile, email, roles } =
          await getUser(id);

        const updatedForm = {
          loginId: loginId,
          password: "",
          username: username,
          groups: groups?.map((group) => group.groupId) ?? [],
          mobile: mobile ? Util.formatPhoneNumber(mobile) : "",
          email: email ?? "",
          roles: roles?.map((role) => role.rolesId) ?? [],
        };

        setUserForm(updatedForm);
        setUserFormOriginal(updatedForm); // 초기값이라 같은 값으로 설정

        if (groups?.length) {
          setSelectedGroups(
            groups.map((item) => {
              return {
                label: item.groupName,
                value: item.groupId,
              };
            })
          );
        }
        if (roles?.length) {
          const selectedRoleList = roles.map((role) => {
            return {
              label: role.rolesName,
              value: role.rolesId,
              /**
               * @description 현재 role이 sortedDropdownOptionsRoles에 없는 경우 부여 권한이 없는 것이므로 disabled 처리
               * @author jh_jeong
               */
              disabled: !sortedDropdownOptionsRoles.some(
                (option) => option.value === role.rolesId
              ),
            };
          });
          setSelectedRoles(selectedRoleList);
          // 선택된 role이 목록 제일 앞단에 위치하도록 정렬
          const sortedRoles = [
            ...selectedRoleList,
            ...sortedDropdownOptionsRoles.filter(
              (value) =>
                !selectedRoleList.some(
                  (selected) => selected.value === value.value
                )
            ),
          ];
          setSortedDropdownOptionsRoles(sortedRoles);
        }

        setIsValidUserForm({
          ...isValidUserForm,
          loginId: {
            ...isValidUserForm.loginId,
            isValidDuplicate: true,
          },
        });
      };

      getUserInfoByUserId(userId);
    },
    [userId]
  );

  useEffect(
    /** 비밀번호 유효성검사 및 비밀번호 확인 체크 */
    function updateIsValidPassword() {
      if (!userForm.password) {
        return;
      }

      const isValidObject = {
        isValidLowerLetter: Util.checkValidateUser(
          "password",
          userForm.password,
          "isValidLowerLetter"
        ),
        isValidLength: Util.checkValidateUser(
          "password",
          userForm.password,
          "isValidLength"
        ),
        isValidSpecialLetter: Util.checkValidateUser(
          "password",
          userForm.password,
          "isValidSpecialLetter"
        ),
        isValidNumber: Util.checkValidateUser(
          "password",
          userForm.password,
          "isValidNumber"
        ),
      };

      setIsValidUserForm({
        ...isValidUserForm,
        password: isValidObject,
        passwordConfirm: {
          isValidEqualPassword: userForm.password === passwordConfirm,
        },
      });
    },
    [userForm.password]
  );

  /** 비밀번호 확인 체크 */
  useEffect(
    function updateIsValidEqualPassword() {
      if (!passwordConfirm) {
        return;
      }
      setIsValidUserForm({
        ...isValidUserForm,
        passwordConfirm: {
          isValidEqualPassword: userForm.password === passwordConfirm,
        },
      });
    },
    [passwordConfirm]
  );

  useEffect(
    /** selectedGroup 바뀌면 userForm에도 적용 */
    function updateGroupsInUserForm() {
      const updatedForm: UserCreateFormType = {
        ...userForm,
        groups: selectedGroups.map((item) => item.value),
      };
      setUserForm(updatedForm);
    },

    [selectedGroups.length]
  );

  useEffect(
    /** selectedRoles 바뀌면 userForm에도 적용 */
    function updateRolesInUserForm() {
      const updatedForm: UserCreateFormType = {
        ...userForm,
        roles: selectedRoles.map((role) => role.value as string),
      };
      setUserForm(updatedForm);
    },
    [selectedRoles]
  );

  const isClickableSubmitButton =
    isValidForCreated &&
    userForm.username.length > 0 &&
    selectedRoles.length > 0 &&
    selectedGroups.length > 0;

  useEffect(
    /** submit button active 로직 : 등록 */
    function updateSubmitButtonActiveState() {
      if (!editMode) {
        setIsAble(isClickableSubmitButton);
      }
    },
    [isClickableSubmitButton]
  );

  /** submit button active 로직 : 수정 */
  useEffect(() => {
    if (!editMode) {
      return;
    }

    const changedUserInfoKey = Object.keys(changedForm);

    if (!changedUserInfoKey.length) {
      setIsAble(false);
      return; // 조건에 맞지 않으면 여기서 실행 중지
    }

    // 이하 코드는 조건에 맞을 때만 실행됨

    const isUsernameValid = () =>
      !changedUserInfoKey.includes("username") || Boolean(userForm.username);

    const isRolesValid = () =>
      !changedUserInfoKey.includes("roles") || selectedRoles.length > 0;

    const isGroupsValid = () =>
      !changedUserInfoKey.includes("groups") || selectedGroups.length > 0;

    const isLoginIdValid = () =>
      !changedUserInfoKey.includes("loginId") ||
      (userForm.loginId &&
        isValidUserForm.loginId.isValidLength &&
        isValidUserForm.loginId.isValidCharacter &&
        isValidUserForm.loginId.isValidDuplicate === true);

    const isPasswordValid = () =>
      !changedUserInfoKey.includes("password") ||
      (userForm.password &&
        isValidUserForm.password.isValidLength &&
        isValidUserForm.password.isValidLowerLetter &&
        isValidUserForm.password.isValidNumber &&
        isValidUserForm.password.isValidSpecialLetter &&
        isValidUserForm.passwordConfirm.isValidEqualPassword);

    const isRequiredFieldsValid = () =>
      userForm.loginId &&
      userForm.username &&
      userForm.roles?.length &&
      userForm.groups?.length;

    const updateIsAble =
      isUsernameValid() &&
      isRolesValid() &&
      isGroupsValid() &&
      isLoginIdValid() &&
      isPasswordValid() &&
      isRequiredFieldsValid();

    setIsAble(Boolean(updateIsAble));
  }, [
    changedForm,
    userForm.username,
    selectedRoles.length > 0,
    selectedGroups.length > 0,
    userForm.password,
    userForm.loginId,
  ]);

  return (
    <Modal
      title={`${editMode ? "Edit" : "Create"} User ${
        editMode && !canModifyUsersRole ? "(read only)" : ""
      }`}
      modalToggle={modalToggle}
      fitSize={true}
      height="max-content"
      maxHeight="80%"
      actionButtons={
        editMode && !canModifyUsersRole
          ? []
          : [
              {
                size: "large",
                width: "12rem",
                color: "primary",
                type: "solid",
                label: editMode ? "Save" : "Create",
                disabled: !isAble || isSubmitted,
                onClick: handleSubmitButtonClick,
              },
            ]
      }
    >
      <StyledUserModalContainer>
        <div className="area">
          <div className="field-area">
            <div className="label-area">
              <div
                className={`label${
                  userForm.loginId &&
                  (!isValidUserForm.loginId.isValidLength ||
                    isValidUserForm.loginId.isValidDuplicate === false)
                    ? " error"
                    : ""
                }`}
              >
                <p>
                  ID<span>{" *"}</span>
                </p>
              </div>
              <Button
                size="small"
                color="strong"
                type="outline"
                disabled={
                  !isValidUserForm.loginId.isValidLength ||
                  !isValidUserForm.loginId.isValidCharacter
                }
                label={
                  isValidUserForm.loginId.isValidDuplicate !== "None"
                    ? isValidUserForm.loginId.isValidDuplicate
                      ? "Available ID"
                      : "Unavailable ID"
                    : "Check ID Availability"
                }
                preIcon={
                  !isValidUserForm.loginId.isValidDuplicate
                    ? "XCircle"
                    : "CheckCircle"
                }
                selected={isValidUserForm.loginId.isValidDuplicate === true}
                error={!isValidUserForm.loginId.isValidDuplicate}
                onClick={handleIdCheckDuplicate}
              />
            </div>
            <TextFieldWithLock
              readOnly={editMode && !canModifyUsersRole}
              widthValue="26rem"
              optionName="loginId"
              optionValue={userForm.loginId}
              optionPlaceholder="Please enter the ID."
              handleOptionInfoChange={handleUserFormChange}
              error={
                userForm.loginId
                  ? isValidUserForm.loginId.isValidLength &&
                    isValidUserForm.loginId.isValidDuplicate !== false
                    ? false
                    : true
                  : false
              }
              maxLength={70}
              minLength={4}
            />
            <div style={{ display: "flex", gap: "0.8rem" }}>
              <CheckBadge
                textNoWrap
                title="At least 4 characters"
                color={getBadgeColor(
                  userForm.loginId,
                  isValidUserForm.loginId.isValidLength
                )}
              />

              <CheckBadge
                textNoWrap
                title="The character '/' is not allowed"
                color={getBadgeColor(
                  userForm.loginId,
                  isValidUserForm.loginId.isValidCharacter
                )}
              />
            </div>
          </div>
        </div>
        <div className="area">
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <TextFieldWithLock
              type="password"
              readOnly={editMode && !canModifyUsersRole}
              required={!editMode}
              optionName="password"
              optionLabel="Password"
              optionValue={userForm.password}
              optionPlaceholder="Please enter the password."
              handleOptionInfoChange={handleUserFormChange}
              maxLength={70}
              minLength={8}
            />

            <div style={{ display: "flex", gap: "12px" }}>
              <CheckBadge
                textNoWrap
                title="Lowercase"
                color={getBadgeColor(
                  userForm.password,
                  isValidUserForm.password.isValidLowerLetter
                )}
              />
              <CheckBadge
                textNoWrap
                title="Number"
                color={getBadgeColor(
                  userForm.password,
                  isValidUserForm.password.isValidNumber
                )}
              />
              <CheckBadge
                textNoWrap
                title="Special character"
                color={getBadgeColor(
                  userForm.password,
                  isValidUserForm.password.isValidSpecialLetter
                )}
              />
              <CheckBadge
                textNoWrap
                title="At least 8 characters"
                color={getBadgeColor(
                  userForm.password,
                  isValidUserForm.password.isValidLength
                )}
              />
            </div>
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <TextFieldWithLock
              type={"password"}
              readOnly={editMode && !canModifyUsersRole}
              required={!editMode}
              optionName="password"
              optionLabel="Confirm password"
              optionValue={passwordConfirm}
              optionPlaceholder="Please enter your password again."
              handleOptionInfoChange={handlePasswordConfirmChange}
              maxLength={70}
              minLength={8}
            />

            <div style={{ display: "flex", gap: "12px" }}>
              <CheckBadge
                textNoWrap
                title="Password match"
                color={getBadgeColor(
                  passwordConfirm,
                  isValidUserForm.passwordConfirm.isValidEqualPassword
                )}
              />
            </div>
          </div>
        </div>
        <div className="area">
          <TextFieldWithLock
            widthValue="26rem"
            readOnly={editMode && !canModifyUsersRole}
            required
            optionName="username"
            optionLabel="User Name"
            optionValue={userForm.username}
            optionPlaceholder="Please enter the username."
            handleOptionInfoChange={handleUserFormChange}
            maxLength={70}
            minLength={1}
          />
          {/* 수정모드인 경우 자신의 그룹정보만 조회 가능 */}
          {(!editMode || (editMode && userId === loginUserId)) && (
            <div className="group-area">
              {!editMode && (
                <SelectBox
                  size="large"
                  label={"Groups"}
                  placeholder={"Add Group"}
                  options={filteredSearchSuggestions.map((item) => {
                    return { label: item, value: item };
                  })}
                  handleValue={(selected) => {
                    handleGroupSelect(selected.value as string);
                  }}
                  required
                  comboBox
                />
              )}
              <ChipArea
                title={!editMode ? undefined : "Groups"}
                selectedList={selectedGroups}
                onDelete={handleSelectedGroupExclude}
                hasClose={!editMode}
                height="7.2rem"
              />
            </div>
          )}
        </div>
        <div className="area row">
          <TextFieldWithLock
            readOnly={editMode && !canModifyUsersRole}
            optionName="mobile"
            optionLabel="Phone Number"
            optionValue={userForm.mobile}
            optionPlaceholder="000 - 0000 - 0000"
            handleOptionInfoChange={handleUserFormChange}
            maxLength={70}
            minLength={0}
          />

          <TextFieldWithLock
            readOnly={editMode && !canModifyUsersRole}
            optionName="email"
            optionLabel="Email"
            optionValue={userForm.email}
            optionPlaceholder="Email @ domain.kr"
            handleOptionInfoChange={handleUserFormChange}
            maxLength={70}
            minLength={0}
          />
        </div>

        {/* 자신의 정보인 경우 롤 조회만 가능 하도록 수정  */}
        <div className="area">
          {editMode && userId === loginUserId ? (
            <ChipArea
              title="Roles"
              selectedList={selectedRoles as DropDownListOptionStringType[]}
              onDelete={() => {}}
              hasClose={false}
              height="7.2rem"
            />
          ) : (
            <CheckboxArea
              disabled={
                (editMode && !canModifyUsersRole) ||
                (editMode && !modifyRolesRole)
              }
              label={`Roles ${
                editMode && !modifyRolesRole ? "(read only)" : ""
              }`}
              required={true}
              options={sortedDropdownOptionsRoles}
              selectedOptions={selectedRoles}
              setSelectedOptions={setSelectedRoles}
            />
          )}
        </div>
      </StyledUserModalContainer>
    </Modal>
  );
};
